import { memo, useCallback, useEffect, useRef, useState } from "react";
import { GalleryVideoObject } from "@/components/Galleries/utils/utils";
import { dispatchGalleryMonetateQEvent } from "@/components/Galleries/components/Analytics/dispatchGalleryMonetateQEvent";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { PlayFilled } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlayFilled";
import styles from "./Gallery.module.scss";

type GalleryVideoProps = {
  video: GalleryVideoObject;
  index: number;
  total: number;
  onSelect?: (index: number) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  // Only used in grid mode, to ensure at most one video plays at a time
  isActive?: boolean;
  onPlayStateChange?: (index: number, isPlaying: boolean) => void;
};

export const GalleryVideo = memo(function GalleryVideo({
  video,
  index,
  total,
  onSelect,
  onKeyDown,
  isActive,
  onPlayStateChange,
}: GalleryVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = useCallback(() => {
    dispatchGalleryMonetateQEvent("VideoInteraction", undefined, undefined);
    videoRef.current?.play();
    setIsPlaying(true);
    onPlayStateChange?.(index, true);
  }, [index, onPlayStateChange]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    onPlayStateChange?.(index, false);
  }, [index, onPlayStateChange]);

  // Another video became active - stop this one so only one plays at a time
  useEffect(() => {
    if (isPlaying && isActive === false) {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
  }, [isActive, isPlaying]);

  if (onSelect) {
    return (
      <div className={styles.carouselVideo} onKeyDown={onKeyDown}>
        <button
          aria-label={`Play video ${index + 1} of ${total}, ${video.title}`}
          className={styles.carouselImagePlayButton}
          data-testid={`gallery:video-play-button-${index}`}
          onClick={() => onSelect(index)}
        >
          <img
            src={formatAmplienceImageURL(`${video.imageUrl}?wid=720&hei=640`)}
            alt=""
            className={styles.image}
            loading="lazy"
          />
        </button>
        <IconButton
          icon={PlayFilled}
          aria-label={`Play video ${index + 1} of ${total}, ${video.title}`}
          data-testid={`gallery:video-play-${index}`}
          className={styles.carouselPlayButton}
          onClick={() => onSelect(index)}
          background="tonal"
          variant="inverted"
        />
      </div>
    );
  }

  return (
    <div className={styles.videoWrapper}>
      <video
        ref={videoRef}
        className={styles.video}
        controls={isPlaying}
        poster={video.posterUrl}
        src={video.videoUrl}
        aria-label={`Video ${index + 1} of ${total}, ${video.title}`}
        data-testid={`gallery:video-${index}`}
        onKeyDown={onKeyDown}
        onPause={handlePause}
        onEnded={handlePause}
      />
      {!isPlaying && (
        <>
          <button
            aria-label={`Play video ${index + 1} of ${total}, ${video.title}`}
            data-testid={`gallery:video-play-button-${index}`}
            className={styles.carouselImagePlayButton}
            onClick={handlePlay}
          ></button>
          <IconButton
            icon={PlayFilled}
            aria-label={`Play video ${index + 1} of ${total}, ${video.title}`}
            data-testid={`gallery:video-play-${index}`}
            className={styles.playButton}
            onClick={handlePlay}
            background="tonal"
            variant="inverted"
          />
        </>
      )}
    </div>
  );
});
